import { DEFAULT_HOUSEHOLD, type Location } from "@larder/shared";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  createLocation,
  ensureProfile,
  readProfile,
  watchMyLocations,
} from "./locations";

/**
 * The user's households, live — and created for them if they have none.
 *
 * `status` distinguishes "still loading" from "genuinely has none" on purpose:
 * showing an empty state for a beat before the real list arrives reads as data
 * loss, which is the worst possible first impression for an app whose whole
 * promise is remembering things for you.
 */
export interface LocationsState {
  status: "loading" | "ready" | "error";
  locations: Location[];
  /** The one the app opens into. Null only while loading. */
  current: Location | null;
  error: string | null;
  select: (id: string) => void;
}

export function useLocations(uid: string | null): LocationsState {
  const [locations, setLocations] = useState<Location[]>([]);
  const [status, setStatus] = useState<LocationsState["status"]>("loading");
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // One attempt per user. Without this, a failing create would be retried on
  // every snapshot forever, and a slow one could create two "Home"s.
  const seeding = useRef<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setLocations([]);
      setStatus("loading");
      return;
    }
    setStatus("loading");
    let live = true;

    ensureProfile(uid)
      .then((p) => {
        if (live) setSelectedId((prev) => prev ?? p.defaultLocationId);
      })
      .catch(() => {
        /* non-fatal: falls back to the first household */
      });

    const stop = watchMyLocations(
      uid,
      (next) => {
        if (!live) return;
        setLocations(next);

        // Nobody should have to name a household before they have seen the
        // app. One is made for them, silently, and renaming lives in settings.
        if (next.length === 0 && seeding.current !== uid) {
          seeding.current = uid;
          createLocation(uid, DEFAULT_HOUSEHOLD).catch((e: Error) => {
            if (!live) return;
            setError(e.message);
            setStatus("error");
          });
          return; // stay "loading" until the household actually arrives
        }
        setStatus("ready");
      },
      (e) => {
        if (!live) return;
        setError(e.message);
        setStatus("error");
      },
    );

    return () => {
      live = false;
      stop();
    };
  }, [uid]);

  const select = useCallback((id: string) => setSelectedId(id), []);
  const current = locations.find((l) => l.id === selectedId) ?? locations[0] ?? null;

  return { status, locations, current, error, select };
}

export { readProfile };
